import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import {
	AdfParseError,
	AdfParser,
	AdfRepairStrategy,
	AdfSchemaValidator,
} from '@atlassian/adf-parser';
import { AdfToolCallsDebugger } from '@atlassian/editor-ai-common/utils/adf-tool-calls-debugger';
import type {
	AiStreamingOrchestratorPlugin,
	StreamingCommand,
	StreamingError,
} from '@atlassian/editor-plugin-ai-streaming-orchestrator';
import type { MultiFormatContentType } from '@atlassian/editor-plugin-ai-streaming-orchestrator/types';
import { FAILURE_REASON } from '@atlassian/editor-plugin-ai/errors';
import type { AgentInfo } from '@atlassian/editor-plugin-ai/types';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import { InlineInputBoxAbortMessageCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/inline-input-box-abort-message-command';
import type { RovoContentBridgeAPI } from '@atlassian/rovo-content-bridge-api/bridge-api';
import type { ClientId } from '@atlassian/rovo-content-bridge-api/client-types';


import type { ActionMetadata, ContentLocation } from '../types/content';
import { log } from '../utils/aifc-logging';
import {
	fireInfiniteLoopDetectedAnalyticsEvent,
	fireParallelToolCallForceFinishedAnalyticsEvent,
} from '../utils/analytics';

import { getParserConfig } from './parser-config';
import {
	FinishAction,
	InitAction,
	PartialStreamParser,
	StreamAction,
} from './partial-stream-parser';
import { PartialStreamParserV2, type ToolCallV2Envelope } from './partial-stream-parser-v2';
import { ToolCallProcessorV2 } from './tool-call-processor-v2';
import {
	createToolProcessorChunkStrategy,
	type ToolProcessorChunkStrategy,
} from './tool-processor-chunk-strategies';

/**
 * Read once at construction time so behaviour stays stable for the duration
 * of one stream processor instance even if the exp-val is toggled.
 */
function isHtmlPlusV2StreamingEnabled(): boolean {
	return expValEquals('platform_editor_use_html_plus_streaming_parser', 'isEnabled', true);
}

/** Default queue timeout (ms) when the Statsig dynamic config is unavailable. */
const DEFAULT_PARALLEL_QUEUE_TIMEOUT_MS = 2000;

function isPositiveNumber(value: unknown): value is number {
	return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/** Statsig dynamic-config name that holds tunable parallel-tool-call parameters. */
const QUEUE_CONFIG_NAME = 'platform_editor_ai_parallel_tool_calls_config';

/**
 * Reads `queueTimeoutMs` from the dedicated Statsig dynamic config. Falls back to
 * `DEFAULT_PARALLEL_QUEUE_TIMEOUT_MS` when the client is not initialized, the
 * config / parameter is missing, or the value is not a positive finite number.
 */
export function getParallelQueueTimeoutMs(): number {
	try {
		if (!FeatureGates.initializeCompleted()) {
			return DEFAULT_PARALLEL_QUEUE_TIMEOUT_MS;
		}
		const dynamicConfig = FeatureGates.getExperiment(QUEUE_CONFIG_NAME);
		const queueTimeoutMs = dynamicConfig?.value?.value;
		return isPositiveNumber(queueTimeoutMs) ? queueTimeoutMs : DEFAULT_PARALLEL_QUEUE_TIMEOUT_MS;
	} catch {
		return DEFAULT_PARALLEL_QUEUE_TIMEOUT_MS;
	}
}

/**
 * The total amount of times we're happy to process the same toolcall command. If more sequential toolcalls exist then
 * what this value is set too, then we have detected a possible infinite loop and we will abort the stream.
 */
const MAX_INFINITE_LOOP_COUNT = 5;
const SKIP_INFINITE_LOOP_ACTIONS = ['insertNodeAfter', 'insertNodeBefore'];

class ToolCallProcessor {
	private actionId: string;
	private accumulated: string = '';
	private parser: AdfParser | undefined;
	private strategy: ToolProcessorChunkStrategy | undefined;
	private command: StreamingCommand;

	constructor(
		private editorApi: PublicPluginAPI<AiStreamingOrchestratorPlugin>,
		private contentLocation: ContentLocation,
		command: StreamingCommand,
	) {
		this.command = command;
		const actionId = this.editorApi?.aiStreamingOrchestrator?.actions.initAction(command, {
			/**
			 * Always hide the loading screen as staging area always shows Rovo conversation
			 * and will already have a loading indicator.
			 */
			hideLoadingScreen: this.contentLocation === 'staging-area',
		});
		if (!actionId) {
			throw new Error('Failed to initialize streaming action for update');
		}
		this.actionId = actionId;
		log('Initialized editor streaming action', { streamType: 'update', actionId, command });

		const schema = this.editorApi.core.sharedState.currentState()?.schema;
		if (
			expValEquals('platform_editor_ai_multi_format_streaming', 'isEnabled', true) ||
			expValEquals('platform_editor_use_html_plus_parser', 'isEnabled', true)
		) {
			const contentType =
				'contentType' in command
					? (command.contentType as MultiFormatContentType | undefined)
					: undefined;
			this.strategy = createToolProcessorChunkStrategy(contentType, schema);
		} else if (schema) {
			this.parser = new AdfParser({
				schemaValidator: new AdfSchemaValidator(schema),
				repairStrategy: new AdfRepairStrategy(schema),
				config: getParserConfig(),
			});
		}
	}

	stream(chunk: string, isFinished: boolean) {
		if (!chunk) {
			// For replaceDoc, an empty value signals "clear the entire document".
			// We must still call streamValue so the streaming orchestrator can dispatch
			// the empty-paragraph fallback (instead of silently no-oping).
			if (this.command.type === 'replaceDoc') {
				this.editorApi.aiStreamingOrchestrator?.actions.streamValue(this.actionId, '');
			}
			return;
		}

		if (
			expValEquals('platform_editor_ai_multi_format_streaming', 'isEnabled', true) ||
			expValEquals('platform_editor_use_html_plus_parser', 'isEnabled', true)
		) {
			this.accumulated += chunk;
			const processedAdf = this.strategy?.processChunk(chunk, isFinished) ?? chunk;
			this.editorApi.aiStreamingOrchestrator?.actions.streamValue(this.actionId, processedAdf);
			log('Streaming content', { actionId: this.actionId, chunk });
			return;
		}

		if (this.parser) {
			try {
				this.parser.processChunk(chunk, isFinished);
			} catch (error) {
				if (error instanceof AdfParseError) {
					// We want to ignore any parsing errors
				} else {
					// We still want to throw any other unexpected errors
					throw error;
				}
			}
			this.editorApi.aiStreamingOrchestrator?.actions.streamValue(
				this.actionId,
				JSON.stringify(this.parser.getPartialDocument()) ?? '',
			);
		} else {
			this.editorApi.aiStreamingOrchestrator?.actions.streamValue(this.actionId, chunk);
		}

		this.accumulated += chunk;
		log('Streaming content', { actionId: this.actionId, chunk });
	}

	finish() {
		// For replaceDoc with an empty value, no StreamAction is emitted by the parser
		// (the "value" string is empty so `valueEnd.partial.length > 0` is never true).
		// We must call streamValue with an empty string so the orchestrator receives at
		// least one stream call and can apply its empty-paragraph fallback instead of
		// silently no-oping.
		if (this.command.type === 'replaceDoc' && this.accumulated === '') {
			this.editorApi.aiStreamingOrchestrator?.actions.streamValue(this.actionId, '');
		}
		this.editorApi?.aiStreamingOrchestrator?.actions.finishAction(this.actionId);
		log('Finished action', { actionId: this.actionId, fullAdf: this.accumulated });
		this.accumulated = '';
	}
}

function assertUnreachable(_x: never) {}

export class DocumentUpdateCommandProcessor {
	/**
	 * v1 parser. Owned even when the v2 gate is on: in that case it lives
	 * inside `parserV2` and the wrapper forwards non-streamable tool calls to
	 * it so today's behaviour for `removeNode`, `setAttrs`, `moveNode`,
	 * `wrap`, etc. is preserved bit-for-bit.
	 */
	private parser: PartialStreamParser;
	/**
	 * v2 hybrid parser, only constructed when the
	 * `platform_editor_use_html_plus_streaming_parser` exp-val is on.
	 * Routes streamable tool calls through the v2 envelope path and forwards
	 * non-streamable ones to the embedded v1 parser.
	 */
	private parserV2: PartialStreamParserV2 | undefined;
	/** Legacy single-active-id v2 processor (used when parallel gate is OFF). */
	private activeV2Processor: ToolCallProcessorV2 | undefined;
	private activeV2EnvelopeId: string | undefined;

	// --- FIFO parallel-queue state (used only when gate is ON) ---
	/** All distinct ids seen so far, in FIFO arrival order. */
	private v2OrderedIds: string[] = [];
	/** Latest envelope snapshot per queued (non-active) id. */
	private v2PendingSnapshotById: Map<string, ToolCallV2Envelope> =
		new Map();
	/** Per-id processor instances (active + already-finished ones still draining). */
	private v2ProcessorById: Map<string, ToolCallProcessorV2> = new Map();
	/** The id currently being actively applied. */
	private v2CurrentActiveId: string | undefined;
	/** Timeout handle for force-finishing a stalled active id. */
	private v2QueueTimeout: ReturnType<typeof setTimeout> | null = null;
	/** Latest observed `name` per envelope id (used for force-finished analytics). */
	private v2ToolCallNameById: Map<string, string> = new Map();
	/** Per-id tracking for force-finished ids; flushed as analytics on stream end. */
	private v2ForceFinishedRecords: Map<
		string,
		{
			/** True if a `status: 'complete'` envelope for this id arrived after the force-finish. */
			completedAfterTimeout: boolean;
			/** Delay (ms) between force-finish and the first late chunk, or null if none arrived. */
			firstLateChunkDelayMs: number | null;
			/** `Date.now()` timestamp (ms since epoch) at which the force-finish occurred. */
			forceFinishedAt: number;
			/** Delay (ms) between force-finish and the most recent late chunk, or null if none arrived. */
			lastLateChunkDelayMs: number | null;
			/** Count of envelopes for this id that arrived after the force-finish. */
			lateChunkCount: number;
			/** The timeout value (ms) that was applied when this id was force-finished. */
			timeoutMs: number;
			/** The `name` field from the originating v2 envelope at force-finish time. */
			toolCallName: string;
		}
	> = new Map();
	/**
	 * Tracks whether the per-session `htmleditor.streaming_contract_resolved`
	 * event has been fired yet. Spec §6.2: fire exactly once per session on
	 * first observed chunk.
	 */
	private streamingContractResolved: boolean = false;
	private readonly useV2Streaming: boolean;
	private currentToolCall: ToolCallProcessor | undefined;
	private currentToolCallName: string | null = null;
	private currentToolCallLocalId: string | null = null;
	private currentRawCommand: Record<string, unknown> | null = null;
	private currentStreamedValue: string = '';
	private accumulated: string = '';
	private lastAction: string | null;
	private hasDetectedInfiniteLoop: boolean = false;
	private loopsDetected: number = 0;
	private debugger: AdfToolCallsDebugger | null = null;
	private actionMetadata: ActionMetadata | null = null;

	constructor(
		private editorApi: PublicPluginAPI<[AnalyticsPlugin, AiStreamingOrchestratorPlugin]>,
		private contentLocation: ContentLocation,
		private clientId: ClientId,
		private rovoBridgeAPI: RovoContentBridgeAPI,
	) {
		this.parser = new PartialStreamParser();
		this.useV2Streaming = isHtmlPlusV2StreamingEnabled();
		if (this.useV2Streaming) {
			// Share the same v1 parser instance so that test mocks set on
			// `this.parser` still observe the v1 fall-through (e.g. when v1
			// tool calls are forwarded by the v2 wrapper).
			this.parserV2 = new PartialStreamParserV2(this.parser);
		}
		this.lastAction = null;
		this.debugger = AdfToolCallsDebugger.getInstance();
	}

	/**
	 * Reset all per-session state.
	 *
	 * If `error` is provided, this is an error-teardown reset (called from
	 * `abort()` or the infinite-loop branch of `finish()`). In that case we
	 * propagate the error through `activeV2Processor.finish(error)` so the
	 * orchestrator dispatches an error event rather than treating the
	 * last-seen partial v2 snapshot as a successful tool-call result. Mirrors
	 * how the v1 path calls `finishAction(actionId, error)` via the queue's
	 * `endStreamCommand`.
	 */
	private reset(error?: StreamingError) {
		this.accumulated = '';
		this.lastAction = null;
		this.currentToolCall = undefined;
		this.currentToolCallName = null;
		this.currentToolCallLocalId = null;
		this.currentRawCommand = null;
		this.currentStreamedValue = '';
		this.hasDetectedInfiniteLoop = false;
		this.loopsDetected = 0;
		this.parser.reset();
		if (this.parserV2) {
			this.parserV2.reset();
		}
		// Finish any leftover v2 processor first so it emits its final
		// `finishAction` to the orchestrator. Propagate `error` so the
		// orchestrator does NOT commit the last partial snapshot to the
		// document as a successful result on abort / error teardown.
		if (this.activeV2Processor) {
			this.activeV2Processor.finish(error);
		}
		this.activeV2Processor = undefined;
		this.activeV2EnvelopeId = undefined;
		// Reset the FIFO parallel-queue state (finishes any in-flight
		// processors that belong to the new scheduler path).
		this.resetParallelQueueState(error);
		this.streamingContractResolved = false;
	}

	private createToolCallProcessor(toolCall: InitAction) {
		const type = toolCall.name;
		const localId = toolCall.localId ?? '';
		const contentType = toolCall.contentType ?? undefined;

		let command: StreamingCommand;
		switch (type) {
			case 'replaceDoc':
				// replaceDoc has no localId — it replaces the entire document.
				// contentType is 'text/html' or 'application/adf+json' (enforced by the parser).
				command = {
					type,
					contentType,
					...(toolCall.noStream && { noStream: true }),
				};
				break;
			case 'replaceNode':
			case 'insertNodeBefore':
			case 'insertNodeAfter':
				command = {
					type,
					localId,
					contentType,
					...(toolCall.noStream && { noStream: true }),
				};
				break;
			case 'removeNode':
				command = { type, localId };
				break;
			case 'removeNodes':
				const localIds = toolCall.localIds;
				if (!localIds) {
					return;
				}
				command = { type, localIds };
				break;
			case 'moveNode':
				const anchorLocalId = toolCall.anchorLocalId;
				const relationship = toolCall.relationship;
				if (!localId || !anchorLocalId || !relationship) {
					return;
				}

				command = { type, localId, anchorLocalId, relationship };
				break;
			case 'appendNode':
			case 'appendNodeToEnd':
				command = { type, contentType };
				break;
			case 'wrap':
			case 'wrapNode':
				const wrapperType = toolCall.wrapperType;
				const wrapperAttrs = toolCall.wrapperAttrs;
				if (!wrapperType || !wrapperAttrs) {
					return;
				}

				command = { type, localId, wrapperType, wrapperAttrs };
				break;
			case 'setAttrs':
				const attrs = toolCall.attrs;
				if (!attrs) {
					return;
				}

				command = { type, localId, attrs };
				break;

			default:
				assertUnreachable(type);
				return;
		}

		return new ToolCallProcessor(this.editorApi, this.contentLocation, command);
	}

	streamChunk(
		chunk: string,
		isFinished: boolean,
		traceId?: string,
		singleInstrumentationId?: string,
		agentInfo?: AgentInfo,
	): void {
		// The last message contains a copy of the full, accumulated tool call structure. We
		// don't want to reprocess this, so we just return instead.
		if (isFinished) {
			return;
		}

		if (this.accumulated.length === 0) {
			this.reset();
			this.editorApi.aiStreamingOrchestrator?.actions.startSession(traceId, {
				singleInstrumentationId,
				promptVersion: fg('platform_editor_ai_promptversion_in_analytics')
					? this.actionMetadata?.promptVersion
					: undefined,
				agentInfo: editorExperiment('platform_editor_agent_mentions', true) ? agentInfo : undefined,
			});
			this.debugger?.startSession(traceId, 'update');
		}

		this.accumulated += chunk;
		return this.stream(chunk, isFinished);
	}

	private stream(chunk: string, isFinished: boolean) {
		// Once we detect an infinite loop and call finish(), we won't be able to recover
		if (this.hasDetectedInfiniteLoop) {
			return;
		}

		// V2 path: feed the hybrid parser when the gate is on AND the chunk
		// looks like it might carry v2 provisional metadata. The primary
		// discriminator per spec §1.4 is the presence of a top-level `"id"`
		// inside a `toolCalls` object; the substring check below is a fast
		// pre-filter to avoid the JSON-array parse work on the vast majority
		// of chunks that are clearly mid-v1-array.
		let results: ReturnType<PartialStreamParser['parse']>;
		const looksLikeV2 =
			this.useV2Streaming && this.parserV2 !== undefined && chunk.includes('"id"');
		if (looksLikeV2 && this.parserV2) {
			results = this.parserV2.feed(chunk);
			const envelopes = this.parserV2.drainV2Envelopes();
			for (const envelope of envelopes) {
				// Spec §6.2: fire `streaming_contract_resolved` exactly once per
				// session, on first observed v2 chunk. Include
				// `streamingContract: 'v2'` for uniform queryability alongside
				// the other v2 analytics events (`v2_envelope_malformed`,
				// `v2_stale_sequence`, `partial_after_complete`).
				if (!this.streamingContractResolved) {
					this.streamingContractResolved = true;
					log('htmleditor.streaming_contract_resolved', {
						streamingContract: 'v2',
						feContractVersion: 2,
						resolvedV2: true,
					});
				}

				if (fg('platform_editor_ai_parallel_tool_calls_v2')) {
					// Force-finish is terminal for an id: record late chunks and discard.
					if (this.v2ForceFinishedRecords.has(envelope.id)) {
						this.recordLateChunkIfForceFinished(envelope);
						continue;
					}
					this.v2ToolCallNameById.set(envelope.id, envelope.name);
					if (!this.v2OrderedIds.includes(envelope.id)) {
						this.v2OrderedIds.push(envelope.id);
					}
					if (!this.v2CurrentActiveId) {
						this.v2CurrentActiveId = envelope.id;
					}
					if (envelope.id === this.v2CurrentActiveId) {
						this.applyActiveV2Envelope(envelope);
					} else {
						// Queue latest snapshot per id; v2 envelopes are full snapshots.
						this.v2PendingSnapshotById.set(envelope.id, envelope);
						this.armParallelQueueTimeout();
					}
				} else {
					// Legacy single-active-id model (spec §2.2 / §3.6). If the
					// envelope's `id` differs from the currently-active id, finalise
					// the previous processor before starting a new one.
					if (
						this.activeV2EnvelopeId !== undefined &&
						this.activeV2EnvelopeId !== envelope.id
					) {
						if (this.activeV2Processor) {
							this.activeV2Processor.finish();
						}
						this.activeV2Processor = undefined;
						this.activeV2EnvelopeId = undefined;
					}

					if (!this.activeV2Processor) {
						this.activeV2Processor = new ToolCallProcessorV2(
							this.editorApi,
							this.contentLocation,
						);
						this.activeV2Processor.bindEnvelopeId(envelope.id);
						this.activeV2EnvelopeId = envelope.id;
					}

					this.activeV2Processor.apply(envelope);

					if (envelope.status === 'complete') {
						// The processor has already called `finishAction`. Clear the
						// active slot so a subsequent (different-id) envelope starts
						// fresh.
						this.activeV2Processor = undefined;
						this.activeV2EnvelopeId = undefined;
					}
				}
			}
		} else {
			results = this.parser.parse(chunk);
		}

		for (const result of results) {
			if (result instanceof InitAction) {
				if (result.localId) {
					if (
						this.lastAction === `${result.name}-${result.localId}` &&
						!SKIP_INFINITE_LOOP_ACTIONS.includes(result.name)
					) {
						this.loopsDetected++;
						if (this.loopsDetected >= MAX_INFINITE_LOOP_COUNT) {
							this.hasDetectedInfiniteLoop = true;
							fireInfiniteLoopDetectedAnalyticsEvent(this.editorApi, result);
							this.debugger?.recordSessionError(
								'infinite_loop_detected',
								`Infinite loop detected for ${result.name} on localId ${result.localId}`,
							);
							return;
						} else {
							this.initToolCallWithDebugger(result);
						}
					} else {
						this.loopsDetected = 0;
						this.initToolCallWithDebugger(result);
					}
					this.lastAction = `${result.name}-${result.localId}`;
				} else {
					this.lastAction = null;
					this.initToolCallWithDebugger(result);
				}
			} else if (result instanceof StreamAction) {
				this.currentToolCall?.stream(result.partialValue, isFinished);
				this.currentStreamedValue += result.partialValue;
			} else if (result instanceof FinishAction) {
				this.currentToolCall?.finish();
				if (this.currentToolCallName) {
					const fullRawCommand = this.currentRawCommand
						? {
								...this.currentRawCommand,
								...(this.currentStreamedValue ? { value: this.currentStreamedValue } : {}),
							}
						: null;
					this.debugger?.recordToolCallSuccess(
						this.currentToolCallName,
						this.currentToolCallLocalId,
						fullRawCommand,
					);
				}
				this.currentToolCallName = null;
				this.currentToolCallLocalId = null;
				this.currentRawCommand = null;
				this.currentStreamedValue = '';
			} else {
				throw new Error('Unknown action type from partial stream parser');
			}
		}
	}

	// ---------------------------------------------------------------------------
	// FIFO parallel-queue helpers (used only when gate is ON)
	// ---------------------------------------------------------------------------

	/**
	 * Apply one v2 envelope for the currently-active tool-call id.
	 * Creates a processor on first use; on `status === 'complete'` advances
	 * to the next queued id.
	 */
	private applyActiveV2Envelope(
		envelope: ToolCallV2Envelope,
	): void {
		let processor = this.v2ProcessorById.get(envelope.id);
		if (!processor) {
			processor = new ToolCallProcessorV2(this.editorApi, this.contentLocation);
			processor.bindEnvelopeId(envelope.id);
			this.v2ProcessorById.set(envelope.id, processor);
		}
		processor.apply(envelope);

		if (envelope.status === 'complete') {
			this.v2ProcessorById.delete(envelope.id);
			this.advanceToNextV2();
		}
	}

	/**
	 * Called when the currently-active id finishes (or is force-finished by
	 * the timeout). Removes the id from the FIFO queue and starts the next
	 * queued id if one is waiting.
	 */
	private advanceToNextV2(): void {
		this.clearParallelQueueTimeout();
		this.v2OrderedIds = this.v2OrderedIds.filter((id) => id !== this.v2CurrentActiveId);
		this.v2CurrentActiveId = undefined;

		const nextId = this.v2OrderedIds.find((id) => this.v2PendingSnapshotById.has(id));
		if (!nextId) {
			return;
		}
		this.v2CurrentActiveId = nextId;
		const snapshot = this.v2PendingSnapshotById.get(nextId);
		this.v2PendingSnapshotById.delete(nextId);
		if (!snapshot) {
			// Defensive: should not happen because we found `nextId` via `.has()`,
			// but if it does, skip this id and try the next one rather than stalling.
			this.advanceToNextV2();
			return;
		}
		this.applyActiveV2Envelope(snapshot);

		// Re-arm only while other ids are still queued behind this one.
		const queueStillHasOthers = this.v2OrderedIds.some(
			(id) => id !== this.v2CurrentActiveId && this.v2PendingSnapshotById.has(id),
		);
		if (queueStillHasOthers) {
			this.armParallelQueueTimeout();
		}
	}

	/** Arms a one-shot timeout that force-finishes the active id if it stalls. */
	private armParallelQueueTimeout(): void {
		if (this.v2QueueTimeout) {
			return;
		}
		const timeoutMs = getParallelQueueTimeoutMs();
		this.v2QueueTimeout = setTimeout(() => {
			this.v2QueueTimeout = null;
			const forcedId = this.v2CurrentActiveId;
			const activeProcessor = forcedId ? this.v2ProcessorById.get(forcedId) : undefined;

			// Skip force-finish if no other id is waiting on the queue.
			const hasPendingOtherIds = this.v2OrderedIds.some(
				(id) => id !== forcedId && this.v2PendingSnapshotById.has(id),
			);
			if (!hasPendingOtherIds) {
				return;
			}

			if (activeProcessor && forcedId) {
				activeProcessor.finish();
				this.v2ProcessorById.delete(forcedId);
				if (!this.v2ForceFinishedRecords.has(forcedId)) {
					this.v2ForceFinishedRecords.set(forcedId, {
						timeoutMs,
						forceFinishedAt: Date.now(),
						lateChunkCount: 0,
						firstLateChunkDelayMs: null,
						lastLateChunkDelayMs: null,
						completedAfterTimeout: false,
						toolCallName: this.v2ToolCallNameById.get(forcedId) ?? '',
					});
				}
				log('htmleditor.parallel_tool_call.force_finished', {
					streamingContract: 'v2',
					envelopeId: forcedId,
					reason: 'queue_timeout',
					timeoutMs,
				});
			}
			this.advanceToNextV2();
		}, timeoutMs);
	}

	/** Updates late-chunk counters for a previously force-finished id. */
	private recordLateChunkIfForceFinished(envelope: ToolCallV2Envelope): void {
		const record = this.v2ForceFinishedRecords.get(envelope.id);
		if (!record) {
			return;
		}
		const delayMs = Date.now() - record.forceFinishedAt;
		record.lateChunkCount += 1;
		if (record.firstLateChunkDelayMs === null) {
			record.firstLateChunkDelayMs = delayMs;
		}
		record.lastLateChunkDelayMs = delayMs;
		if (envelope.status === 'complete') {
			record.completedAfterTimeout = true;
		}
	}

	/** Clears any armed parallel-queue timeout. */
	private clearParallelQueueTimeout(): void {
		if (this.v2QueueTimeout) {
			clearTimeout(this.v2QueueTimeout);
			this.v2QueueTimeout = null;
		}
	}

	/** Reset all FIFO parallel-queue state fields. */
	private resetParallelQueueState(error?: StreamingError): void {
		this.clearParallelQueueTimeout();
		for (const processor of this.v2ProcessorById.values()) {
			processor.finish(error);
		}
		this.v2ProcessorById.clear();
		this.v2PendingSnapshotById.clear();
		this.v2OrderedIds = [];
		this.v2CurrentActiveId = undefined;
		// Drop force-finished records on abort; `finish()` flushes them first.
		this.v2ForceFinishedRecords.clear();
		this.v2ToolCallNameById.clear();
	}

	/** Fires one `parallelToolCall.forceFinished` analytics event per force-finished id. */
	private flushForceFinishedAnalyticsEvents(): void {
		if (this.v2ForceFinishedRecords.size === 0) {
			return;
		}
		for (const [envelopeId, record] of this.v2ForceFinishedRecords) {
			fireParallelToolCallForceFinishedAnalyticsEvent(this.editorApi, {
				envelopeId,
				timeoutMs: record.timeoutMs,
				lateChunkCount: record.lateChunkCount,
				firstLateChunkDelayMs: record.firstLateChunkDelayMs,
				lastLateChunkDelayMs: record.lastLateChunkDelayMs,
				completedAfterTimeout: record.completedAfterTimeout,
				toolCallName: record.toolCallName,
			});
		}
		this.v2ForceFinishedRecords.clear();
	}

	private static buildRawCommand(result: InitAction): Record<string, unknown> {
		const raw: Record<string, unknown> = { name: result.name };
		if (result.localId != null) {
			raw.localId = result.localId;
		}
		if (result.anchorLocalId != null) {
			raw.anchorLocalId = result.anchorLocalId;
		}
		if (result.relationship != null) {
			raw.relationship = result.relationship;
		}
		if (result.contentType != null) {
			raw.contentType = result.contentType;
		}
		if (result.wrapperType != null) {
			raw.wrapperType = result.wrapperType;
		}
		if (result.wrapperAttrs != null) {
			raw.wrapperAttrs = result.wrapperAttrs;
		}
		if (result.attrs != null) {
			raw.attrs = result.attrs;
		}
		return raw;
	}

	private initToolCallWithDebugger(result: InitAction) {
		this.currentToolCallName = result.name;
		this.currentToolCallLocalId = result.localId ?? null;
		this.currentRawCommand = DocumentUpdateCommandProcessor.buildRawCommand(result);

		try {
			this.currentToolCall = this.createToolCallProcessor(result);
			if (!this.currentToolCall) {
				this.debugger?.recordToolCallError(
					result.name,
					result.localId ?? null,
					'invalid_command_found',
					`Failed to create tool call processor for ${result.name}: missing required attributes`,
					this.currentRawCommand,
				);
			}
		} catch (error) {
			const message = error instanceof Error ? error.message : String(error);
			this.debugger?.recordToolCallError(
				result.name,
				result.localId ?? null,
				'tool_call_init_failed',
				message,
				this.currentRawCommand,
			);
		}
	}

	async finish(): Promise<void> {
		if (this.hasDetectedInfiniteLoop) {
			this.rovoBridgeAPI.send(InlineInputBoxAbortMessageCommand, {
				receiverId: ROVO_CHAT_CLIENT_ID,
				senderId: this.clientId,
			});
			this.debugger?.endSession('errored');
			// Propagate an error to any in-flight v2 processor so its
			// `finishAction(actionId, error)` reflects the infinite-loop
			// teardown rather than committing the last partial snapshot as a
			// successful result.
			this.reset({
				failureReason: FAILURE_REASON.ADF_STREAMING_ERROR,
				errorKey: 'infinite_loop_detected',
			});
			await this.editorApi.aiStreamingOrchestrator?.actions.endSession();
		} else {
			this.debugger?.endSession('completed');
			this.accumulated = '';
			// Flush an unfinished v2 processor if the stream ended before a
			// `status: 'complete'` envelope was seen (e.g. backend aborted
			// early). Spec §3.6.
			if (this.activeV2Processor) {
				this.activeV2Processor.finish();
				this.activeV2Processor = undefined;
				this.activeV2EnvelopeId = undefined;
			}
			// Best-effort drain of any queued v2 snapshots.
			if (fg('platform_editor_ai_parallel_tool_calls_v2')) {
				// Finish the active processor first (it already had apply() called; just needs finish()).
				const activeProc = this.v2CurrentActiveId
					? this.v2ProcessorById.get(this.v2CurrentActiveId)
					: undefined;
				if (activeProc && this.v2CurrentActiveId) {
					activeProc.finish();
					this.v2ProcessorById.delete(this.v2CurrentActiveId);
				}
				// Drain queued (non-active) snapshots — these haven't had apply() yet.
				for (const id of this.v2OrderedIds) {
					if (id === this.v2CurrentActiveId) {
						continue; // already finished above
					}
					const snapshot = this.v2PendingSnapshotById.get(id);
					if (snapshot) {
						const drainProcessor = new ToolCallProcessorV2(
							this.editorApi,
							this.contentLocation,
						);
						drainProcessor.bindEnvelopeId(id);
						drainProcessor.apply(snapshot);
						if (snapshot.status !== 'complete') {
							drainProcessor.finish();
						}
					}
				}
				// Must run BEFORE reset (which clears the records map).
				this.flushForceFinishedAnalyticsEvents();
				this.resetParallelQueueState();
			}
			this.streamingContractResolved = false;
			await this.editorApi.aiStreamingOrchestrator?.actions.endSession();
			this.lastAction = null;
			this.currentToolCall = undefined;
			this.currentToolCallName = null;
			this.currentToolCallLocalId = null;
			this.currentRawCommand = null;
			this.currentStreamedValue = '';
			this.parser.reset();
			if (this.parserV2) {
				this.parserV2.reset();
			}
		}
	}

	abort(reason?: 'aborted' | 'timeout'): void {
		this.debugger?.endSession(reason ?? 'aborted');
		// Propagate an error to any in-flight v2 processor so the orchestrator
		// dispatches an error event for the last partial snapshot rather than
		// silently committing it. Matches the error-propagation semantics of
		// the v1 path's `finishAction(actionId, error)` flow.
		this.reset({
			failureReason: FAILURE_REASON.UNKNOWN,
			errorKey: reason ?? 'aborted',
		});
		this.editorApi.aiStreamingOrchestrator?.actions.abortSession();
	}

	setActionMetadata(metadata: ActionMetadata): void {
		this.actionMetadata = metadata;
		log('Set action metadata', { metadata });
	}
}
